import React from 'react';
import { WhatsAppPhoneRow } from '../../molecules/whatsapp/whatsapp-phone-row';
import type { IntegrationAddress } from 'src/api/generated';
import { formatDate } from 'src/modules/shared/utils/formatting/date.util';
import { Card } from 'src/modules/shared/components/atoms/cards';
import { CardBody } from 'src/modules/shared/components/atoms/cards/card-body';
import { CardHeader } from 'src/modules/shared/components/atoms/cards/card-header';
import { Text } from 'src/modules/shared/components/atoms/text';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { SiWhatsapp } from 'react-icons/si';
import { PiCalendar, PiTrashLight } from 'react-icons/pi';
import { CircleDisplay } from 'src/modules/shared/components/atoms/circle-display';
import { Alert } from 'src/modules/shared/components/atoms/alert';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';

interface WhatsAppAccountCardProps {
  /** Provider account owning these phone rows. */
  integrationProviderId: string;
  /** Phone rows belonging to the same account. */
  phones: IntegrationAddress[];
  /** Translated association or offboarding warning, when applicable. */
  onboardingWarning?: string;
  /** Whether authorization or billing provenance prevents removal. */
  removalBlocked?: boolean;
  /** Whether a removal request is already pending. */
  removing?: boolean;
  /** Requests removal through the parent's guarded action. */
  onRemove: () => void;
  /** Permission-aware lifecycle actions supplied by the account list. */
  actions?: React.ReactNode;
}

/** Composes account details, onboarding safety and individual phone-row molecules. */
export const WhatsAppAccountCard: React.FC<WhatsAppAccountCardProps> = ({
  integrationProviderId,
  phones,
  onboardingWarning,
  removalBlocked = false,
  removing = false,
  onRemove,
  actions,
}) => {
  const { t } = useI18n();
  if (phones.length === 0) return null;
  // Use the first phone to get integration-level data
  const firstPhone = phones[0];

  return (
    <Card shadow="md" variant="primary" appearance="outline">
      <CardHeader>
        <div className="flex items-center gap-3">
          <CircleDisplay
            icon={<SiWhatsapp />}
            variant="whatsapp-subtle"
            size="md"
          />

          <div className="flex-1 flex flex-col gap-1">
            <Text size="h6" weight="medium">
              {firstPhone.displayName ??
                t('settings.integrations.whatsapp.accounts.defaultName')}
            </Text>

            <Text size="small" color="secondary" weight="regular">
              {t('settings.integrations.whatsapp.accounts.identifier', {
                id: integrationProviderId,
              })}
            </Text>

            <div className="flex items-center gap-2 text-secondary-600">
              <PiCalendar className="h-4 w-4" />
              <Text size="small" color="secondary" weight="regular">
                {t('settings.integrations.whatsapp.accounts.connectedAt', {
                  date: formatDate(firstPhone.createdAt),
                })}
              </Text>
            </div>
          </div>

          {!removalBlocked ? (
            <Button
              variant="danger"
              size="sm"
              disabled={removing}
              onClick={onRemove}
            >
              <div className="flex items-center gap-2">
                <PiTrashLight className="h-4 w-4" />
                {t('shared.actions.delete')}
              </div>
            </Button>
          ) : null}
        </div>
      </CardHeader>

      <CardBody>
        <div className="flex flex-col gap-2">
          {onboardingWarning ? (
            <Alert
              role="alert"
              size="sm"
              variant="warning"
              message={onboardingWarning}
            />
          ) : null}

          <Text size="small" weight="medium">
            {t('settings.integrations.whatsapp.accounts.phoneCount', {
              count: phones.length,
            })}
          </Text>

          <div className="flex flex-col gap-2">
            {phones.map((phone) => (
              <WhatsAppPhoneRow key={phone.providerId} phone={phone} />
            ))}
          </div>
          {actions}
        </div>
      </CardBody>
    </Card>
  );
};
